import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Plus, Trash2, Search, CalendarIcon, Pencil, Receipt, Plane, DollarSign, Filter, FileDown } from 'lucide-react';
import { ExportButton } from '@/components/ExportButton';
import { VentaFormDialog, VentaDeleteDialog } from '@/components/ventas/VentaFormDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VentasPage = () => {
  const { getAuthHeaders } = useAuth();
  const [ventas, setVentas] = useState([]);
  const [gestores, setGestores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedVenta, setSelectedVenta] = useState(null);
  const [editingVenta, setEditingVenta] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ start: null, end: null });
  
  const [formData, setFormData] = useState({
    gestor_id: '',
    amount: '',
    tickets_count: '',
    route_origin: '',
    route_destination: '',
    passenger_name: '',
    passengers: [''],
    pnr_code: '',
    airline: '',
    flight_date_departure: new Date(),
    flight_date_return: null,
    apply_commission: false,
    commission_pct: ''
  });
  const [airports, setAirports] = useState({ origins: [], destinations: [] });
  const [airlines, setAirlines] = useState([]);
  const [pricingRoutes, setPricingRoutes] = useState([]);

  useEffect(() => {
    fetchData();
    fetchOptions();
  }, [periodFilter, customDateRange]);

  const fetchOptions = async () => {
    try {
      const headers = getAuthHeaders();
      const [airlinesRes, pricingRes] = await Promise.all([
        axios.get(`${API_URL}/options/airlines`, { headers }),
        axios.get(`${API_URL}/admin/pricing`, { headers })
      ]);
      setAirlines(airlinesRes.data);
      // Build routes from pricing guides
      const routes = pricingRes.data.filter(p => p.is_active !== false);
      setPricingRoutes(routes);
      const origins = [...new Set(routes.map(r => {
        const parts = r.service_name.split(/[-–]/);
        return parts[0]?.trim();
      }).filter(Boolean))];
      const destinations = [...new Set(routes.map(r => {
        const parts = r.service_name.split(/[-–]/);
        return parts[1]?.trim();
      }).filter(Boolean))];
      setAirports({ origins, destinations });
    } catch (error) {
      console.error('Error loading options', error);
    }
  };

  const getDateRangeForPeriod = (period) => {
    const now = new Date();
    if (period === 'weekly') {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay() + 1); // Monday
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { start: format(start, 'yyyy-MM-dd'), end: format(end, 'yyyy-MM-dd') };
    } else if (period === 'monthly') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { start: format(start, 'yyyy-MM-dd'), end: format(end, 'yyyy-MM-dd') };
    } else if (period === 'annual') {
      return { start: `${now.getFullYear()}-01-01`, end: `${now.getFullYear()}-12-31` };
    }
    return null;
  };

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      let params = new URLSearchParams();
      
      if (periodFilter === 'custom' && customDateRange.start && customDateRange.end) {
        params.append('start_date', format(customDateRange.start, 'yyyy-MM-dd'));
        params.append('end_date', format(customDateRange.end, 'yyyy-MM-dd'));
      } else if (periodFilter !== 'all') {
        const range = getDateRangeForPeriod(periodFilter);
        if (range) {
          params.append('start_date', range.start);
          params.append('end_date', range.end);
        }
      }
      
      const queryStr = params.toString() ? `?${params.toString()}` : '';
      const [ventasRes, gestoresRes] = await Promise.all([
        axios.get(`${API_URL}/ventas${queryStr}`, { headers }),
        axios.get(`${API_URL}/gestores`, { headers })
      ]);
      setVentas(ventasRes.data);
      setGestores(gestoresRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const validPassengers = formData.passengers.filter(p => p.trim() !== '');
      const displayName = validPassengers.length > 1 
        ? validPassengers.join(', ') 
        : (validPassengers[0] || formData.passenger_name);
      
      const payload = {
        ...formData,
        passenger_name: displayName,
        passengers: validPassengers.length > 0 ? validPassengers : [formData.passenger_name],
        amount: parseFloat(formData.amount),
        tickets_count: parseInt(formData.tickets_count),
        flight_date_departure: format(formData.flight_date_departure, 'yyyy-MM-dd'),
        flight_date_return: formData.flight_date_return ? format(formData.flight_date_return, 'yyyy-MM-dd') : null,
        pnr_code: formData.pnr_code || null,
        airline: formData.airline || null,
        commission_pct: formData.apply_commission && formData.commission_pct ? parseFloat(formData.commission_pct) : null,
      };
      delete payload.apply_commission;

      if (editingVenta) {
        await axios.put(`${API_URL}/ventas/${editingVenta.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Venta actualizada exitosamente');
      } else {
        await axios.post(`${API_URL}/ventas`, payload, { headers: getAuthHeaders() });
        toast.success('Venta registrada exitosamente');
      }
      fetchData();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar venta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (venta) => {
    setEditingVenta(venta);
    setFormData({
      gestor_id: venta.gestor_id,
      amount: venta.amount.toString(),
      tickets_count: venta.tickets_count.toString(),
      route_origin: venta.route_origin,
      route_destination: venta.route_destination,
      passenger_name: venta.passenger_name,
      passengers: venta.passengers || [venta.passenger_name],
      pnr_code: venta.pnr_code || '',
      airline: venta.airline || '',
      flight_date_departure: new Date(venta.flight_date_departure),
      flight_date_return: venta.flight_date_return ? new Date(venta.flight_date_return) : null,
      apply_commission: !!(venta.commission_pct && venta.commission_pct > 0),
      commission_pct: venta.commission_pct?.toString() || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/ventas/${selectedVenta.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Venta eliminada');
      fetchData();
      setIsDeleteDialogOpen(false);
      setSelectedVenta(null);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleStatusChange = async (ventaId, newStatus) => {
    try {
      await axios.put(`${API_URL}/ventas/${ventaId}/status?status=${newStatus}`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Estado actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingVenta(null);
    setFormData({
      gestor_id: '',
      amount: '',
      tickets_count: '',
      route_origin: '',
      route_destination: '',
      passenger_name: '',
      passengers: [''],
      pnr_code: '',
      airline: '',
      flight_date_departure: new Date(),
      flight_date_return: null
    });
  };

  const addPassenger = () => {
    setFormData({ ...formData, passengers: [...formData.passengers, ''] });
  };

  const removePassenger = (index) => {
    if (formData.passengers.length <= 1) return;
    const updated = formData.passengers.filter((_, i) => i !== index);
    setFormData({ ...formData, passengers: updated });
  };

  const updatePassenger = (index, value) => {
    const updated = [...formData.passengers];
    updated[index] = value;
    setFormData({ ...formData, passengers: updated });
  };

  const handleDownloadPDF = async (ventaId) => {
    try {
      const response = await axios.get(`${API_URL}/ventas/${ventaId}/prereserva-pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prereserva_${ventaId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al generar PDF');
    }
  };

  const filteredVentas = ventas.filter(v => 
    v.passenger_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.route_origin.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.route_destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.gestor_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  const totalVentas = ventas.reduce((sum, v) => sum + v.amount, 0);
  const totalPasajes = ventas.reduce((sum, v) => sum + v.tickets_count, 0);

  const getPeriodLabel = () => {
    switch(periodFilter) {
      case 'weekly': return 'Esta Semana';
      case 'monthly': return 'Este Mes';
      case 'annual': return 'Este Año';
      case 'custom': return 'Rango Personalizado';
      default: return 'Todo el Tiempo';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="ventas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Registro de Ventas
          </h1>
          <p className="text-slate-500 mt-1">Gestiona todas las ventas de pasajes • {getPeriodLabel()}</p>
        </div>
        <div className="flex items-center gap-3">
          <ExportButton type="table" tableName="ventas" />
          <Button 
            onClick={() => setIsDialogOpen(true)}
            className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-semibold"
            data-testid="add-venta-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Venta
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Receipt className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Ventas</p>
              <p className="text-2xl font-bold text-slate-900">{ventas.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Plane className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Pasajes Vendidos</p>
              <p className="text-2xl font-bold text-slate-900">{totalPasajes}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Receipt className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Monto Total</p>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(totalVentas)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Período:</span>
        </div>
        <Select value={periodFilter} onValueChange={(val) => {
          setPeriodFilter(val);
          if (val !== 'custom') setCustomDateRange({ start: null, end: null });
        }}>
          <SelectTrigger className="w-44" data-testid="ventas-period-filter">
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todo</SelectItem>
            <SelectItem value="daily">Diario</SelectItem>
            <SelectItem value="weekly">Semanal</SelectItem>
            <SelectItem value="monthly">Mensual</SelectItem>
            <SelectItem value="annual">Anual</SelectItem>
            <SelectItem value="custom">Rango personalizado</SelectItem>
          </SelectContent>
        </Select>

        {periodFilter === 'custom' && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-auto" data-testid="ventas-custom-date-picker">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {customDateRange.start ? (
                  customDateRange.end ? (
                    `${format(customDateRange.start, 'dd/MM/yyyy')} - ${format(customDateRange.end, 'dd/MM/yyyy')}`
                  ) : format(customDateRange.start, 'dd/MM/yyyy')
                ) : 'Seleccionar fechas'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                selected={{ from: customDateRange.start, to: customDateRange.end }}
                onSelect={(range) => setCustomDateRange({ start: range?.from, end: range?.to })}
                locale={es}
                numberOfMonths={2}
              />
              <div className="p-2 border-t flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => {
                  setCustomDateRange({ start: null, end: null });
                  setPeriodFilter('all');
                }}>
                  Limpiar
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Buscar por pasajero, ruta o gestor..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white"
          data-testid="search-ventas"
        />
      </div>

      {/* Table */}
      <Card className="shadow-sm" data-testid="ventas-table">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Gestor</TableHead>
                <TableHead>Pasajero(s)</TableHead>
                <TableHead>PNR</TableHead>
                <TableHead>Ruta</TableHead>
                <TableHead>Aerolínea</TableHead>
                <TableHead>Ida</TableHead>
                <TableHead>Vuelta</TableHead>
                <TableHead className="text-right">Pasajes</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="w-28">Acc.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredVentas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                    No se encontraron ventas
                  </TableCell>
                </TableRow>
              ) : (
                filteredVentas.map((venta) => (
                  <TableRow key={venta.id} className="hover:bg-slate-50">
                    <TableCell>
                      <span className="font-medium text-slate-900">{venta.gestor_name}</span>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900">
                        {venta.passengers && venta.passengers.length > 1 ? (
                          <div className="space-y-0.5">
                            {venta.passengers.map((p, i) => (
                              <div key={`${venta.id}-pax-${i}`} className="text-sm">{i+1}. {p}</div>
                            ))}
                          </div>
                        ) : venta.passenger_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm text-blue-700">{venta.pnr_code || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <span className="font-medium">{venta.route_origin}</span>
                        <span className="text-blue-500">→</span>
                        <span className="font-medium">{venta.route_destination}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{venta.airline || '-'}</TableCell>
                    <TableCell className="text-slate-600">{venta.flight_date_departure}</TableCell>
                    <TableCell className="text-slate-600">{venta.flight_date_return || '-'}</TableCell>
                    <TableCell className="text-right text-slate-600">{venta.tickets_count}</TableCell>
                    <TableCell className="text-right font-semibold text-slate-900">{formatCurrency(venta.amount)}</TableCell>
                    <TableCell>
                      <Badge className={venta.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {venta.status === 'completed' ? 'Completado' : 'Pendiente'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleDownloadPDF(venta.id)} title="PDF Pre-reserva" data-testid={`pdf-venta-${venta.id}`}>
                          <FileDown className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(venta)} title="Editar" data-testid={`edit-venta-${venta.id}`}>
                          <Pencil className="w-4 h-4 text-blue-500" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => { setSelectedVenta(venta); setIsDeleteDialogOpen(true); }} data-testid={`delete-venta-${venta.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <VentaFormDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        editingVenta={editingVenta}
        formData={formData}
        setFormData={setFormData}
        gestores={gestores}
        airlines={airlines}
        pricingRoutes={pricingRoutes}
        submitting={submitting}
        addPassenger={addPassenger}
        removePassenger={removePassenger}
        updatePassenger={updatePassenger}
      />

      {/* Delete Confirmation Dialog */}
      <VentaDeleteDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        ventaName={selectedVenta?.passenger_name}
      />
    </div>
  );
};

export default VentasPage;
